import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiErrorMessage } from '@/containers/sequences/api-error.util';
import { useAuth } from '@clerk/react';
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { toast } from 'sonner';
import { iconArrowsRotate, iconSpinnerThird, iconTrash } from '@/lib/icons';

type AllowlistedDomain = {
  domain: string;
  note: string | null;
  created_by: string | null;
  created_at: string | null; // ISO timestamp
};

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/admin/trial-domain-allowlist`;

export const TRIAL_DOMAIN_ALLOWLIST_EXPLANATION =
  'Company domains here always get a free trial, even if someone else from the same company started one in the last 30 days. Personal domains like gmail.com are never limited.';

function formatAddedAt(created_at: string | null): string {
  if (!created_at) return 'unknown';
  const date = new Date(created_at);
  return Number.isNaN(date.getTime()) ? 'unknown' : date.toLocaleDateString();
}

export function AdminTrialDomainAllowlistPanel({
  showHeader = true,
}: {
  showHeader?: boolean;
}) {
  const { getToken } = useAuth();

  const [domains, setDomains] = useState<AllowlistedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // One place for the token, headers, and error envelope ({error:{message}}
  // or legacy {detail}) so the API's 422 text reaches the form verbatim.
  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const token = await getToken();
      const res = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(apiErrorMessage({ data }) || `Failed (${res.status})`);
      }
      return res;
    },
    [getToken]
  );

  const loadDomains = useCallback(async () => {
    setLoading(true);
    try {
      const res = await call('');
      const data = (await res.json()) as { domains: AllowlistedDomain[] };
      setDomains(data.domains || []);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to load trial domains'
      );
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => {
    void loadDomains();
  }, [loadDomains]);

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    const domain = newDomain.trim();
    if (!domain) return;
    const note = newNote.trim();
    setAdding(true);
    setAddError(null);
    try {
      const res = await call('', {
        method: 'POST',
        body: JSON.stringify(note ? { domain, note } : { domain }),
      });
      const row = (await res.json()) as AllowlistedDomain;
      // Re-adding a domain updates its note server-side; mirror that here.
      setDomains((prev) => [
        row,
        ...prev.filter((d) => d.domain !== row.domain),
      ]);
      setNewDomain('');
      setNewNote('');
      toast.success(`${row.domain} will always get a free trial`);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (domain: string) => {
    if (
      !window.confirm(
        `Remove ${domain}? New signups from it will be limited to one free trial per 30 days again.`
      )
    )
      return;
    setRemoving(domain);
    try {
      await call(`/${encodeURIComponent(domain)}`, { method: 'DELETE' });
      setDomains((prev) => prev.filter((d) => d.domain !== domain));
      toast.success(`Removed ${domain}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className='space-y-4'>
      {showHeader && (
        <div className='space-y-1'>
          <h2 className='text-xl font-semibold text-foreground'>
            Trial domains
          </h2>
          <p className='text-sm text-muted-foreground'>
            {TRIAL_DOMAIN_ALLOWLIST_EXPLANATION}
          </p>
        </div>
      )}

      <form onSubmit={(e) => void handleAdd(e)} className='space-y-1.5'>
        <div className='flex flex-col gap-2 sm:flex-row'>
          <Input
            aria-label='Domain'
            placeholder='acme.com'
            value={newDomain}
            onChange={(e) => {
              setNewDomain(e.target.value);
              setAddError(null);
            }}
            aria-invalid={addError ? true : undefined}
            className='sm:w-56'
          />
          <Input
            aria-label='Note'
            placeholder='Note (optional)'
            value={newNote}
            maxLength={500}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Button type='submit' disabled={adding || !newDomain.trim()}>
            {adding && (
              <AppIcon
                icon={iconSpinnerThird}
                className='mr-1.5 animate-spin'
                style={{ fontSize: 13 }}
              />
            )}
            Add domain
          </Button>
        </div>
        {addError && (
          <p role='alert' className='text-sm text-destructive'>
            {addError}
          </p>
        )}
      </form>

      <div className='flex items-center justify-between'>
        <span className='text-sm text-muted-foreground'>
          {loading
            ? 'Loading…'
            : `${domains.length} domain${domains.length === 1 ? '' : 's'}`}
        </span>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void loadDomains()}
          disabled={loading}
        >
          <AppIcon
            icon={iconArrowsRotate}
            className={`mr-1.5 ${loading ? 'animate-spin' : ''}`}
            style={{ fontSize: 13 }}
          />
          Refresh
        </Button>
      </div>

      {!loading && domains.length === 0 ? (
        <div className='rounded-lg border border-border px-3 py-8 text-center text-sm text-muted-foreground'>
          No domains are on the allowlist.
        </div>
      ) : (
        <div className='overflow-x-auto rounded-lg border border-border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Added by</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className='w-0' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.map((entry) => (
                <TableRow key={entry.domain}>
                  <TableCell className='font-mono text-[13px] font-medium text-foreground'>
                    {entry.domain}
                  </TableCell>
                  <TableCell className='text-muted-foreground'>
                    {entry.note || '—'}
                  </TableCell>
                  <TableCell className='text-muted-foreground'>
                    {entry.created_by || 'unknown'}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-muted-foreground'>
                    {formatAddedAt(entry.created_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant='ghost'
                      size='sm'
                      aria-label={`Remove ${entry.domain}`}
                      onClick={() => void handleRemove(entry.domain)}
                      disabled={removing === entry.domain}
                    >
                      <AppIcon
                        icon={
                          removing === entry.domain
                            ? iconSpinnerThird
                            : iconTrash
                        }
                        className={
                          removing === entry.domain ? 'animate-spin' : ''
                        }
                        style={{ fontSize: 13 }}
                      />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
